import { z } from 'zod';

/**
 * Schemas Zod de los filtros de Reportes (usados por `validateSearch` de la
 * ruta y por los formularios de filtro). Las fechas viajan como ISO `YYYY-MM-DD`
 * (string); el backend deriva la granularidad del rango si no se especifica.
 */

export const granularidadSchema = z.enum(['dia', 'semana', 'mes']);

export const reporteTabSchema = z.enum([
  'incidencias',
  'estadisticas',
  'expedientes',
  'cumplimiento',
  'gestion',
]);
export type ReporteTab = z.infer<typeof reporteTabSchema>;

/** Dimensión de agrupación (eje X / porciones) de estadísticas (ADR-040). */
export const estadisticaDimensionSchema = z.enum([
  'tiempo',
  'estado',
  'proyecto',
  'control',
  'categoria',
  'supuesto',
  'origen',
]);
export type EstadisticaDimension = z.infer<typeof estadisticaDimensionSchema>;

/** Segmentación opcional: una serie por valor (apiladas / multi-línea). */
export const estadisticaSegmentoSchema = z.enum(['estado', 'origen']);
export type EstadisticaSegmento = z.infer<typeof estadisticaSegmentoSchema>;

export const chartTipoSchema = z.enum(['line', 'bar', 'stackedBar', 'donut', 'area']);

/** Search params de la página de reportes (compartidos entre pestañas). */
export const filtrosReporteSchema = z.object({
  tab: reporteTabSchema.catch('incidencias'),
  desde: z.string().optional(),
  hasta: z.string().optional(),
  proyectoId: z.coerce.number().int().positive().optional(),
});
export type FiltrosReporte = z.infer<typeof filtrosReporteSchema>;

/** Filtros del lookup de incidencias (resumen / lead-time / series). */
export interface FiltrosReporteIncidencias {
  proyectoId?: number;
  desde?: string;
  hasta?: string;
  granularidad?: z.infer<typeof granularidadSchema>;
}

/**
 * Parámetros del reporte de estadísticas parametrizables (ADR-040):
 * `GET /reportes/incidencias/estadisticas`. `dimension` es obligatorio;
 * `segmentarPor` no puede coincidir con `dimension` (400 del backend — la UI
 * lo previene deshabilitando la opción). `granularidad` solo aplica a
 * `dimension=tiempo`; `limite` (top-N) a las dimensiones de cardinalidad
 * alta. `tipo` fuerza el gráfico; sin él, el backend lo deriva.
 */
export interface FiltrosReporteEstadisticas {
  dimension: z.infer<typeof estadisticaDimensionSchema>;
  segmentarPor?: z.infer<typeof estadisticaSegmentoSchema>;
  proyectoId?: number;
  estados?: string[];
  controlId?: number;
  categoriaId?: number;
  supuestoId?: number;
  origen?: 'Web' | 'Mobile';
  desde?: string;
  hasta?: string;
  granularidad?: z.infer<typeof granularidadSchema>;
  tipo?: z.infer<typeof chartTipoSchema>;
  limite?: number;
}

/** Filtros del portafolio de cumplimiento. */
export interface FiltrosReporteCumplimiento {
  estados?: string[];
  tipoInfraestructuraId?: number;
}

/** Filtros de gestión (rango sobre la bitácora). */
export interface FiltrosReporteGestion {
  desde?: string;
  hasta?: string;
  accion?: string;
  usuarioId?: number;
  granularidad?: z.infer<typeof granularidadSchema>;
}
